import type { SubLedger, SubLedgerType, SystemYear, AssetLookupText, Supplier, FinancialsSupplierAddress, Project, CostCentre, NominalAccount, Currency } from '@/types/financials';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getApiUrl } from '@/lib/api-config';

// Mock data for demo mode
const mockSubLedgers: SubLedger[] = [
  { id: '1', code: 'SL001', description: 'General Ledger', type: 'Nominal' },
  { id: '2', code: 'SL002', description: 'Fixed Assets', type: 'Nominal' },
  { id: '3', code: 'SL003', description: 'Inventory', type: 'Nominal' },
  { id: '4', code: 'SL004', description: 'Accounts Payable', type: 'Purchase' },
  { id: '5', code: 'SL005', description: 'Accounts Receivable', type: 'Sales' },
];

const mockSystemYears: SystemYear[] = [
  { id: '1', label: '2025', startDate: '2025-01-01', endDate: '2025-12-31', nominal: 2025 },
  { id: '2', label: '2024', startDate: '2024-01-01', endDate: '2024-12-31', nominal: 2024 },
  { id: '3', label: '2023', startDate: '2023-01-01', endDate: '2023-12-31', nominal: 2023 },
  { id: '4', label: '2022', startDate: '2022-01-01', endDate: '2022-12-31', nominal: 2022 },
  { id: '5', label: '2021', startDate: '2021-01-01', endDate: '2021-12-31', nominal: 2021 },
];

const mockAssetLookupText: Record<string, AssetLookupText[]> = {
  'ASSORT1': [
    { id: '1', origin: 'ASSORT1', text: 'Department A', sortOrder: 1 },
    { id: '2', origin: 'ASSORT1', text: 'Department B', sortOrder: 2 },
    { id: '3', origin: 'ASSORT1', text: 'Department C', sortOrder: 3 },
  ],
  'ASSORT2': [
    { id: '4', origin: 'ASSORT2', text: 'Building 1', sortOrder: 1 },
    { id: '5', origin: 'ASSORT2', text: 'Building 2', sortOrder: 2 },
    { id: '6', origin: 'ASSORT2', text: 'Building 3', sortOrder: 3 },
  ],
  'ASSORT3': [
    { id: '7', origin: 'ASSORT3', text: 'Project Alpha', sortOrder: 1 },
    { id: '8', origin: 'ASSORT3', text: 'Project Beta', sortOrder: 2 },
    { id: '9', origin: 'ASSORT3', text: 'Project Gamma', sortOrder: 3 },
  ],
  'ASSORT4': [
    { id: '10', origin: 'ASSORT4', text: 'Cost Center 100', sortOrder: 1 },
    { id: '11', origin: 'ASSORT4', text: 'Cost Center 200', sortOrder: 2 },
    { id: '12', origin: 'ASSORT4', text: 'Cost Center 300', sortOrder: 3 },
  ],
  'ASSORT5': [
    { id: '13', origin: 'ASSORT5', text: 'Region North', sortOrder: 1 },
    { id: '14', origin: 'ASSORT5', text: 'Region South', sortOrder: 2 },
    { id: '15', origin: 'ASSORT5', text: 'Region East', sortOrder: 3 },
    { id: '16', origin: 'ASSORT5', text: 'Region West', sortOrder: 4 },
  ],
};

const mockSuppliers: Supplier[] = [
  { primaryKey: 1, code: 'SUP001', name: 'Acme Corporation', inactive: false, balance: 15000.00, currencyCode: 'GBP' },
  { primaryKey: 2, code: 'SUP002', name: 'Tech Solutions Ltd', inactive: false, balance: 8500.50, currencyCode: 'GBP' },
  { primaryKey: 3, code: 'SUP003', name: 'Office Supplies Inc', inactive: false, balance: 2300.00, currencyCode: 'GBP' },
  { primaryKey: 4, code: 'SUP004', name: 'Industrial Equipment Co', inactive: false, balance: 45000.00, currencyCode: 'GBP' },
  { primaryKey: 5, code: 'SUP005', name: 'Global Logistics', inactive: true, balance: 0, currencyCode: 'GBP' },
  { primaryKey: 6, code: 'SUP006', name: 'Premium Parts Ltd', inactive: false, balance: 12750.25, currencyCode: 'GBP' },
];

const mockSupplierAddresses: Record<string, FinancialsSupplierAddress[]> = {
  'SUP001': [
    {
      summarySection: { primaryKey: 1, number: 1, accountCode: 'SUP001', isOrder: true, isRemittance: false, isDelivery: true, completeContactName: 'John Smith' },
      details: { address: '123 Main Street', town: 'London', county: 'Greater London', country: 'UK', postCode: 'EC1A 1BB', telephone: '020 1234 5678', email: 'orders@acme.com' },
    },
    {
      summarySection: { primaryKey: 2, number: 2, accountCode: 'SUP001', isOrder: false, isRemittance: true, isDelivery: false, completeContactName: 'Finance Dept' },
      details: { address: '456 Finance Road', town: 'Manchester', county: 'Greater Manchester', country: 'UK', postCode: 'M1 1AA', telephone: '0161 234 5678', email: 'finance@acme.com' },
    },
  ],
  'SUP002': [
    {
      summarySection: { primaryKey: 3, number: 1, accountCode: 'SUP002', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Sarah Johnson' },
      details: { address: '789 Tech Park', town: 'Cambridge', county: 'Cambridgeshire', country: 'UK', postCode: 'CB1 2AB', telephone: '01onal 345 6789', email: 'info@techsolutions.com' },
    },
  ],
  'SUP003': [
    {
      summarySection: { primaryKey: 4, number: 1, accountCode: 'SUP003', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Mike Brown' },
      details: { address: '321 Office Lane', town: 'Birmingham', county: 'West Midlands', country: 'UK', postCode: 'B1 1AA', telephone: '0121 456 7890', email: 'sales@officesupplies.com' },
    },
  ],
  'SUP004': [
    {
      summarySection: { primaryKey: 5, number: 1, accountCode: 'SUP004', isOrder: true, isRemittance: false, isDelivery: true, completeContactName: 'Industrial Sales' },
      details: { address: '555 Industrial Estate', town: 'Sheffield', county: 'South Yorkshire', country: 'UK', postCode: 'S1 1AA', telephone: '0114 567 8901', email: 'orders@industrial.com' },
    },
    {
      summarySection: { primaryKey: 6, number: 2, accountCode: 'SUP004', isOrder: false, isRemittance: true, isDelivery: false, completeContactName: 'Accounts Payable' },
      details: { address: '556 Industrial Estate', town: 'Sheffield', county: 'South Yorkshire', country: 'UK', postCode: 'S1 1AB', telephone: '0114 567 8902', email: 'accounts@industrial.com' },
    },
  ],
  'SUP006': [
    {
      summarySection: { primaryKey: 7, number: 1, accountCode: 'SUP006', isOrder: true, isRemittance: true, isDelivery: true, completeContactName: 'Parts Department' },
      details: { address: '999 Parts Avenue', town: 'Leeds', county: 'West Yorkshire', country: 'UK', postCode: 'LS1 1AA', telephone: '0113 678 9012', email: 'parts@premiumparts.com' },
    },
  ],
};

const mockCostCentres: CostCentre[] = [
  { code: 'CC001', name: 'Head Office' },
  { code: 'CC002', name: 'Manufacturing' },
  { code: 'CC003', name: 'Sales & Marketing' },
  { code: 'CC004', name: 'IT Department' },
  { code: 'CC005', name: 'Finance' },
  { code: 'CC006', name: 'Human Resources' },
  { code: 'CC007', name: 'Research & Development' },
  { code: 'CC008', name: 'Warehouse' },
  { code: 'CC009', name: 'Customer Service' },
  { code: 'CC010', name: 'Administration' },
];

const mockProjects: Project[] = [
  { code: 'PRJ001', name: 'Office Renovation' },
  { code: 'PRJ002', name: 'IT Infrastructure Upgrade' },
  { code: 'PRJ003', name: 'New Product Launch' },
  { code: 'PRJ004', name: 'Marketing Campaign 2025' },
  { code: 'PRJ005', name: 'Warehouse Expansion' },
  { code: 'PRJ006', name: 'Digital Transformation' },
  { code: 'PRJ007', name: 'Customer Portal Development' },
  { code: 'PRJ008', name: 'Fleet Management System' },
  { code: 'PRJ009', name: 'Sustainability Initiative' },
  { code: 'PRJ010', name: 'Employee Training Program' },
];

const mockNominalAccounts: NominalAccount[] = [
  { id: '1', code: '1000', name: 'Fixed Assets - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '2', code: '1001', name: 'Fixed Assets - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '3', code: '1100', name: 'Motor Vehicles - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '4', code: '1101', name: 'Motor Vehicles - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '5', code: '1200', name: 'Office Equipment - Cost', doNotUse: false, currencyCode: 'GBP' },
  { id: '6', code: '1201', name: 'Office Equipment - Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
  { id: '7', code: '6000', name: 'Depreciation Expense', doNotUse: false, currencyCode: 'GBP' },
  { id: '8', code: '6100', name: 'Depreciation - Motor Vehicles', doNotUse: false, currencyCode: 'GBP' },
  { id: '9', code: '6200', name: 'Depreciation - Office Equipment', doNotUse: false, currencyCode: 'GBP' },
  { id: '10', code: '7000', name: 'Disposal of Assets', doNotUse: false, currencyCode: 'GBP' },
  { id: '11', code: '7100', name: 'Profit/Loss on Disposal', doNotUse: false, currencyCode: 'GBP' },
  { id: '12', code: '1300', name: 'Bank Account', doNotUse: false, currencyCode: 'GBP' },
  { id: '13', code: '1400', name: 'Work in Progress', doNotUse: false, currencyCode: 'GBP' },
  { id: '14', code: '3000', name: 'Revaluation Reserve', doNotUse: false, currencyCode: 'GBP' },
  { id: '15', code: '3100', name: 'Revaluation Surplus', doNotUse: false, currencyCode: 'GBP' },
  { id: '16', code: '9999', name: 'Inactive Account', doNotUse: true, currencyCode: 'GBP' },
];

const mockCurrencies: Currency[] = [
  { primaryKey: 1, symbol: '£', description: 'British Pound', isoCode: 'GBP', isEuroCurrency: false, currencyType: 'Home' },
  { primaryKey: 2, symbol: '$', description: 'US Dollar', isoCode: 'USD', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 3, symbol: '€', description: 'Euro', isoCode: 'EUR', isEuroCurrency: true, currencyType: 'Foreign' },
  { primaryKey: 4, symbol: 'A$', description: 'Australian Dollar', isoCode: 'AUD', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 5, symbol: 'C$', description: 'Canadian Dollar', isoCode: 'CAD', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 6, symbol: 'NZ$', description: 'New Zealand Dollar', isoCode: 'NZD', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 7, symbol: '¥', description: 'Japanese Yen', isoCode: 'JPY', isEuroCurrency: false, currencyType: 'Foreign' },
  { primaryKey: 8, symbol: 'CHF', description: 'Swiss Franc', isoCode: 'CHF', isEuroCurrency: false, currencyType: 'Foreign' },
];

// Helper function to get current data source mode from localStorage
// In live mode (or when mode is not set), use API; only use demo when explicitly set to 'demo'
const getDataSource = (): 'demo' | 'api' => {
  const mode = localStorage.getItem('app-mode');
  if (mode === 'demo') return 'demo';
  return 'api'; // Default to API for 'live' or missing/unknown values
};

// GraphQL response types
interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

interface SubLedgerSummaryGraphQL {
  code: string;
  description: string;
  auditNo?: number;
  creditNo?: number;
  invoiceNo?: number;
  type?: string;
}

interface YearSummaryGraphQL {
  primaryKey: number;
  yearLabel: string;
  nominalYear: number;
  salesYear?: number;
  purchaseYear?: number;
}

interface SystemLookupTextGraphQL {
  primaryKey: number;
  systemLookupTextType: string;
  value: string;
}

interface SupplierSummaryGraphQL {
  primaryKey: number;
  code: string;
  name: string;
  inactive?: boolean;
  balance?: number;
  businessType?: number;
  currencyCode?: string;
  currencySymbol?: string;
  sortKey?: string;
  userSort1?: string;
  userSort2?: string;
  userSort3?: string;
  creditLimit?: number;
}

interface SupplierAddressGraphQL {
  summarySection: {
    primaryKey: number;
    uniqueId?: string;
    number?: number;
    accountCode?: string;
    isOrder?: boolean;
    isRemittance?: boolean;
    isStatement?: boolean;
    isDelivery?: boolean;
    isDeliveryDefault?: boolean;
    completeContactName?: string;
    contactDetails?: {
      title?: string;
      firstName?: string;
      surname?: string;
      salute?: string;
      jobTitle?: string;
      initials?: string;
      contact?: string;
      telephone?: string;
      mobile?: string;
      email?: string;
      fax?: string;
    };
  };
  details: {
    address?: string;
    town?: string;
    county?: string;
    country?: string;
    postCode?: string;
    telephone?: string;
    mobile?: string;
    email?: string;
    fax?: string;
  };
  additionalDetails?: {
    purchaseAnalysisCode?: string;
    notes?: string;
    doNotUse?: boolean;
    dateEdited?: string;
  };
}

interface CostCentreSummaryGraphQL {
  primaryKey: number;
  projectCode?: string;
  code: string;
  name: string;
  status?: string;
}

class FinancialsGraphQLService {
  private authHeader?: string;

  private getBackendApiBaseUrl(): string {
    // Use the same logic as data-service.ts to determine the correct backend URL
    const apiUrlFromConfig = getApiUrl();
    const envUrl = import.meta.env.VITE_API_URL;
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

    // In E2B environment, always use the detected backend URL
    // Otherwise, use the configured URL or env URL as fallback
    const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);

    return baseUrl.replace(/\/$/, '');
  }

  private async fetchBackendAPI<T>(endpoint: string): Promise<T> {
    const baseUrl = this.getBackendApiBaseUrl();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId ?? 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if ((errorData as { message?: string }).message) {
            errorMessage = (errorData as { message: string }).message;
          }
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to API at ${baseUrl}. Please ensure the backend server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  private getGraphQLEndpoint(): string {
    // Use environment variable for Financials GraphQL endpoint if explicitly set
    const envUrl = import.meta.env.VITE_FINANCIALS_GRAPHQL_URL;
    if (envUrl) {
      return this.normalizeEndpoint(envUrl);
    }

    // Detect local development by hostname
    if (typeof window !== 'undefined') {
      const host = window.location.hostname.toLowerCase();
      const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';

      if (isLocal) {
        // For local development, use localhost GraphQL endpoint if available
        return this.normalizeEndpoint('http://localhost:50522/graphql');
      }
    }

    // For preprod and any other non-local hosted environment, use the Financials GraphQL API
    return this.normalizeEndpoint('https://devapi.erp.accessacloud.com/financials-graphql');
  }

  private normalizeEndpoint(endpoint: string): string {
    try {
      const url = new URL(endpoint);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return endpoint.replace(/\/$/, '');
    } catch {
      return endpoint.replace(/\/$/, '');
    }
  }

  private async buildGraphQLHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Product': 'Financials',
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    // Use the saved Financials instance ID if available, otherwise fall back to the platform instance ID
    const financialsInstanceId = authConfig.financialsInstanceId;
    const fallbackInstanceId = authConfig.instanceId ?? 'default-instance';
    headers['X-Instance-Id'] = financialsInstanceId ?? fallbackInstanceId;

    // Add user context headers for Financials API
    if (authConfig.email) {
      headers['X-Email'] = authConfig.email;
    }
    if (authConfig.organisationId) {
      headers['X-Organisation-Id'] = authConfig.organisationId;
    }

    return headers;
  }

  private async parseGraphQLResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `Financials GraphQL API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if ((errorData as { message?: string }).message) {
          errorMessage = (errorData as { message: string }).message;
        }
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    const result: GraphQLResponse<T> = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map(e => e.message).join(', ');
      throw new Error(`GraphQL errors: ${errorMessages}`);
    }

    if (!result.data) {
      throw new Error('No data returned from GraphQL query');
    }

    return result.data;
  }

  private async executeGraphQL<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const endpoint = this.getGraphQLEndpoint();
    const headers = await this.buildGraphQLHeaders();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
      });

      return await this.parseGraphQLResponse<T>(response);
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to Financials GraphQL API at ${endpoint}. Please ensure the Financials server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  async getSubLedgers(type?: SubLedgerType): Promise<SubLedger[]> {
    if (getDataSource() === 'demo') {
      // Filter mock data by type if provided
      if (type) {
        return mockSubLedgers.filter(sl => sl.type === type);
      }
      return mockSubLedgers;
    }

    try {
      const params = type ? `?type=${encodeURIComponent(type)}` : '';
      const data = await this.fetchBackendAPI<SubLedger[]>(`/api/financials/sub-ledgers${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch sub ledgers from backend API:', error);
      throw error;
    }
  }

  async getSystemYears(sort?: string): Promise<SystemYear[]> {
    if (getDataSource() === 'demo') {
      // Return mock data, sorted if requested
      const years = [...mockSystemYears];
      if (sort === 'desc') {
        return years.sort((a, b) => b.nominal - a.nominal);
      } else if (sort === 'asc') {
        return years.sort((a, b) => a.nominal - b.nominal);
      }
      return years;
    }

    try {
      const params = sort ? `?sort=${encodeURIComponent(sort)}` : '';
      const data = await this.fetchBackendAPI<SystemYear[]>(`/api/financials/system-years${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch system years from backend API:', error);
      throw error;
    }
  }

  async getAssetLookupText(origin: string): Promise<AssetLookupText[]> {
    if (getDataSource() === 'demo') {
      // Return mock data for the specified origin
      return mockAssetLookupText[origin] ?? [];
    }

    try {
      const query = `
        query GetSystemLookupText {
          config {
            lists {
              systemLookupText {
                items {
                  primaryKey
                  systemLookupTextType
                  value
                }
              }
            }
          }
        }
      `;

      interface SystemLookupTextResponse {
        config: {
          lists: {
            systemLookupText: {
              items: SystemLookupTextGraphQL[];
            };
          };
        };
      }

      const data = await this.executeGraphQL<SystemLookupTextResponse>(query);
      const items = data.config?.lists?.systemLookupText?.items ?? [];

      // Filter by origin and map to AssetLookupText interface
      const filtered = items.filter(item => item.systemLookupTextType === origin);

      return filtered.map((item, index) => ({
        id: item.primaryKey.toString(),
        origin: item.systemLookupTextType,
        text: item.value,
        sortOrder: index + 1, // GraphQL doesn't provide sortOrder, using index
      }));
    } catch (error) {
      console.error('Failed to fetch asset lookup text from GraphQL API:', error);
      throw error;
    }
  }

  async getSuppliers(includeInactive?: boolean): Promise<Supplier[]> {
    if (getDataSource() === 'demo') {
      // Filter out inactive suppliers unless explicitly requested
      if (includeInactive) {
        return mockSuppliers;
      }
      return mockSuppliers.filter(s => !s.inactive);
    }

    try {
      const params = includeInactive ? '?includeInactive=true' : '';
      const data = await this.fetchBackendAPI<Supplier[]>(`/api/suppliers${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch suppliers from backend API:', error);
      throw error;
    }
  }

  async getSupplierAddresses(supplierCode: string): Promise<FinancialsSupplierAddress[]> {
    if (getDataSource() === 'demo') {
      // Return mock data for the specified supplier
      return mockSupplierAddresses[supplierCode] ?? [];
    }

    try {
      const params = `?supplierCode=${encodeURIComponent(supplierCode)}`;
      const data = await this.fetchBackendAPI<FinancialsSupplierAddress[]>(`/api/financials/supplier-addresses${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch supplier addresses from backend API:', error);
      throw error;
    }
  }

  async getProjects(): Promise<Project[]> {
    if (getDataSource() === 'demo') {
      return mockProjects;
    }

    try {
      const data = await this.fetchBackendAPI<Project[]>('/api/financials/projects');
      return data;
    } catch (error) {
      console.error('Failed to fetch projects from backend API:', error);
      throw error;
    }
  }

  async getCostCentres(): Promise<CostCentre[]> {
    if (getDataSource() === 'demo') {
      return mockCostCentres;
    }

    try {
      const data = await this.fetchBackendAPI<CostCentre[]>('/api/financials/cost-centres');
      return data;
    } catch (error) {
      console.error('Failed to fetch cost centres from backend API:', error);
      throw error;
    }
  }

  async getNominalAccounts(currencyCode?: string): Promise<NominalAccount[]> {
    if (getDataSource() === 'demo') {
      if (currencyCode) {
        return mockNominalAccounts.filter(a => a.currencyCode === currencyCode);
      }
      return mockNominalAccounts;
    }

    try {
      const params = currencyCode ? `?currencyCode=${encodeURIComponent(currencyCode)}` : '';
      const data = await this.fetchBackendAPI<NominalAccount[]>(`/api/financials/nominal-accounts${params}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch nominal accounts from backend API:', error);
      throw error;
    }
  }

  async getCurrencies(): Promise<Currency[]> {
    if (getDataSource() === 'demo') {
      return mockCurrencies;
    }

    try {
      const data = await this.fetchBackendAPI<Currency[]>('/api/financials/currencies');
      return data;
    } catch (error) {
      console.error('Failed to fetch currencies from backend API:', error);
      throw error;
    }
  }
}

export const financialsGraphQLService = new FinancialsGraphQLService();

// Export as financialsService for drop-in replacement of REST service
export const financialsService = financialsGraphQLService;
